/**
 * Operator probe for "한/영 key does nothing in the text box" (live class,
 * 10-22: a student on Windows + Whale could only type Latin letters until
 * they picked another tool and the text tool again).
 *
 * It watches the core's text editor (`textarea.excalidraw-wysiwyg`) from the
 * outside and reports once per text box when the 한/영 key was pressed but
 * nothing afterwards went through the IME. Only counts and element
 * descriptors are reported, never what the student typed.
 */

export type TextInputProbeDetail = Record<
  string,
  string | number | boolean | null | string[]
>;

export type TextInputProbeOptions = {
  report: (kind: "ime_stuck", detail: TextInputProbeDetail) => void;
  /** The core's active tool, for telling the click-to-create path apart. */
  getActiveTool?: () => { type: string; locked: boolean } | null;
  now?: () => number;
};

/** Plain letters typed after the toggle before calling it stuck. */
const LETTERS_AFTER_TOGGLE = 3;
const MAX_TOGGLE_SIGNATURES = 5;

type Session = {
  editor: HTMLTextAreaElement;
  startedAt: number;
  /** What held focus just before this text box, and how it was opened. */
  from: string;
  pointer: string;
  focusIns: number;
  focusOuts: number;
  toggles: number;
  toggleSignatures: string[];
  composingKeys: number;
  compositions: number;
  letters: number;
  lettersSinceToggle: number;
  reported: boolean;
};

const isTextEditor = (
  target: EventTarget | null,
): target is HTMLTextAreaElement =>
  target instanceof HTMLTextAreaElement &&
  target.classList.contains("excalidraw-wysiwyg");

/** `TAG.class` of an element, short enough for a diagnostic field. */
const describe = (target: EventTarget | null): string => {
  if (!(target instanceof Element)) {
    return target === null ? "none" : "other";
  }
  const className =
    typeof target.className === "string"
      ? target.className.trim().split(/\s+/)[0] ?? ""
      : "";
  return `${target.tagName}${className ? `.${className}` : ""}`.slice(0, 80);
};

/** Windows reports 한/영 as HangulMode/Lang1 (keyCode 21), and keyboards
 * that put it on the right Alt as AltRight. */
const isToggleKey = (event: KeyboardEvent) =>
  event.key === "HangulMode" ||
  event.code === "Lang1" ||
  event.keyCode === 21 ||
  event.code === "AltRight";

const isPlainLetter = (event: KeyboardEvent) =>
  !event.ctrlKey &&
  !event.metaKey &&
  !event.altKey &&
  /^[a-z]$/i.test(event.key);

export const attachTextInputProbe = (
  target: Document,
  options: TextInputProbeOptions,
): (() => void) => {
  const now = options.now ?? Date.now;
  let session: Session | null = null;
  let lastPointer = "none";
  let sessionsSinceLoad = 0;

  const sessionFor = (
    editor: HTMLTextAreaElement,
    related: EventTarget | null,
  ): Session => {
    if (session?.editor !== editor) {
      sessionsSinceLoad += 1;
      session = {
        editor,
        startedAt: now(),
        from: describe(related),
        pointer: lastPointer,
        focusIns: 0,
        focusOuts: 0,
        toggles: 0,
        toggleSignatures: [],
        composingKeys: 0,
        compositions: 0,
        letters: 0,
        lettersSinceToggle: 0,
        reported: false,
      };
    }
    return session;
  };

  const maybeReport = (current: Session) => {
    if (
      current.reported ||
      current.toggles === 0 ||
      current.compositions > 0 ||
      current.composingKeys > 0 ||
      current.lettersSinceToggle < LETTERS_AFTER_TOGGLE
    ) {
      return;
    }
    current.reported = true;
    const tool = options.getActiveTool?.() ?? null;
    options.report("ime_stuck", {
      toggles: current.toggles,
      toggleKeys: current.toggleSignatures,
      letters: current.letters,
      ageMs: now() - current.startedAt,
      focusIns: current.focusIns,
      focusOuts: current.focusOuts,
      from: current.from,
      pointer: current.pointer,
      tool: tool ? `${tool.type}${tool.locked ? ":locked" : ""}` : null,
      docFocused: target.hasFocus(),
      isActive: target.activeElement === current.editor,
      textBoxes: sessionsSinceLoad,
    });
  };

  const onPointerDown = (event: PointerEvent) => {
    lastPointer = `${describe(event.target)} x${event.detail}`.slice(0, 80);
  };

  const onFocusIn = (event: FocusEvent) => {
    if (isTextEditor(event.target)) {
      sessionFor(event.target, event.relatedTarget).focusIns += 1;
    }
  };

  const onFocusOut = (event: FocusEvent) => {
    if (isTextEditor(event.target) && session?.editor === event.target) {
      session.focusOuts += 1;
    }
  };

  const onCompositionStart = (event: CompositionEvent) => {
    if (isTextEditor(event.target)) {
      sessionFor(event.target, null).compositions += 1;
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!isTextEditor(event.target)) {
      return;
    }
    const current = sessionFor(event.target, null);
    if (isToggleKey(event)) {
      current.toggles += 1;
      const signature = `${event.key}|${event.code}|${event.keyCode}`;
      if (
        !current.toggleSignatures.includes(signature) &&
        current.toggleSignatures.length < MAX_TOGGLE_SIGNATURES
      ) {
        current.toggleSignatures.push(signature.slice(0, 80));
      }
      return;
    }
    if (event.isComposing || event.keyCode === 229) {
      current.composingKeys += 1;
      return;
    }
    if (isPlainLetter(event)) {
      current.letters += 1;
      if (current.toggles > 0) {
        current.lettersSinceToggle += 1;
        maybeReport(current);
      }
    }
  };

  const listenerOptions = { capture: true } as const;
  target.addEventListener("pointerdown", onPointerDown, listenerOptions);
  target.addEventListener("focusin", onFocusIn, listenerOptions);
  target.addEventListener("focusout", onFocusOut, listenerOptions);
  target.addEventListener(
    "compositionstart",
    onCompositionStart,
    listenerOptions,
  );
  target.addEventListener("keydown", onKeyDown, listenerOptions);
  return () => {
    target.removeEventListener("pointerdown", onPointerDown, listenerOptions);
    target.removeEventListener("focusin", onFocusIn, listenerOptions);
    target.removeEventListener("focusout", onFocusOut, listenerOptions);
    target.removeEventListener(
      "compositionstart",
      onCompositionStart,
      listenerOptions,
    );
    target.removeEventListener("keydown", onKeyDown, listenerOptions);
    session = null;
  };
};
